import { Injectable } from '@angular/core';
import posthog from 'posthog-js';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import {
  BoardGate,
  RemoveUserFromWorkspaceRequest,
  UpdateUnipileRequest,
} from '../../../../../backend/src/core/board/board.gate';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { LambdaService } from '../../shared/core/lambda.service';
import { CoreItemType } from '../core-item/core-item';
import { PostHogEvent } from '../posthog/posthog.event';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { CoreItemService } from './../core-item/core-item.service';
import { Board } from './board';
import { BoardFuncs } from './board.func';

@Injectable({
  providedIn: 'root',
})
export class BoardService {
  constructor(
    private coreItemService: CoreItemService,
    private workspaceHolderService: WorkspaceHolderService,
    private lambdaService: LambdaService
  ) {}

  private boardChangeSubject = new BehaviorSubject<Board | Board[] | undefined>(undefined);

  readonly resourceName = ResourceName.board;

  cleanCache() {
    if (this.boardChangeSubject) {
      this.boardChangeSubject.unsubscribe();
    }

    this.boardChangeSubject = new BehaviorSubject<Board | Board[] | undefined>(undefined);
  }

  updateBoard$(board: Board) {
    posthog.capture(PostHogEvent.positionUpdate);
    return this.coreItemService.updateCoreItem$(board).pipe(
      tap((data) => {
        this.emitBoardChangesInCache(data as Board);
      })
    );
  }

  updateUnipile$(accountId: string) {
    const request: UpdateUnipileRequest = {
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      accountId: accountId,
    };

    return this.lambdaService.run$(this.resourceName, BoardGate.actions.updateUnipile, request).pipe(
      tap((data) => {
        this.emitBoardChangesInCache(data);
      })
    );
  }

  emitBoardChangesInCache(board: Board | Board[]) {
    this.boardChangeSubject.next(board);
  }

  currentBoardOnChange$(): Observable<Board | Board[] | undefined> {
    return this.boardChangeSubject.asObservable();
  }

  getAllBoardList$(hasArchived = false) {
    return this.currentBoardOnChange$().pipe(
      map(() => {
        const boardList =
          this.coreItemService.cachedItemList
            ?.filter((c) => c.coreType === CoreItemType.board)
            .map((c) => c as Board) ?? [];
        if (hasArchived) {
          return boardList;
        }

        const result = boardList.filter((c) => !c.isArchived);
        result.sort((a, b) =>
          BoardFuncs.getNameWithoutFirstEmoji(a).localeCompare(BoardFuncs.getNameWithoutFirstEmoji(b))
        );
        return result;
      })
    );
  }

  getAllUserList$() {
    return this.getAllBoardList$().pipe(
      map((boardList) => {
        const result = boardList.filter((c) => BoardFuncs.isPersonal(c) && !c.userBoardConfig?.isRemoved);
        result.sort((a, b) =>
          BoardFuncs.getNameWithoutFirstEmoji(a).localeCompare(BoardFuncs.getNameWithoutFirstEmoji(b))
        );
        return result;
      })
    );
  }

  getRemovedUserList$() {
    return this.getAllBoardList$().pipe(
      map((boardList) => {
        const result = boardList.filter((c) => BoardFuncs.isPersonal(c) && c.userBoardConfig?.isRemoved);
        result.sort((a, b) =>
          BoardFuncs.getNameWithoutFirstEmoji(a).localeCompare(BoardFuncs.getNameWithoutFirstEmoji(b))
        );
        return result;
      })
    );
  }

  removeFromWorkspace$(board: Board) {
    const userId = board.userBoardConfig?.userId;

    if (userId) {
      const request: RemoveUserFromWorkspaceRequest = {
        workspaceId: this.workspaceHolderService.currentWorkspace().id,
        userId,
      };
      return this.lambdaService.run$(this.resourceName, BoardGate.actions.removeUserFromWorkspace, request).pipe(
        tap(() => {
          board.userBoardConfig!.isRemoved = true;
          this.emitBoardChangesInCache(board);
        })
      );
    } else {
      board.isArchived = true;
      return this.updateBoard$(board);
    }
  }
}
